import { useTranslation } from '@shared/localization';
import { useEffect, useMemo, useState } from 'react';

import { PaymentMethodsPage } from '@/components/pages/payment-methods/PaymentMethodsPage';
import { SessionExpired } from '@/components/pages/SessionExpired';
import { PaymentProcessing } from '@/components/pages/status-pages/PaymentProcessing';
import { PaymentSuccessful } from '@/components/pages/status-pages/PaymentSuccessful';
import { StoredInstrumentsPage } from '@/components/pages/stored-instruments/StoredInstrumentsPage';
import { PaymentIntentFlow } from '@/schemas/PaymentIntent';
import NavigationService, {
  InternalPage,
  Page,
} from '@/services/navigation/NavigationService';

import { ErrorHandler } from './components/common/errors/ErrorHandler';
import { BankTransferDetailsPage } from './components/pages/bank-transfer/BankTransferDetailsPage';
import { FinvoiceDetailsPage } from './components/pages/finvoice/FinvoiceDetailsPage';
import { PaymentOnDeliveryDetailsPage } from './components/pages/payment-on-delivery/PaymentOnDeliveryDetailsPage';
import { LsvMandateSummaryPage } from './components/payment-providers/lsv';
import { PostFinanceMandateSummaryPage } from './components/payment-providers/postfinance';
import { SepaMandateConsentPage } from './components/payment-providers/sepa/SepaMandateConsentPage';
import PaymentStore from './services/payments/PaymentStore';

export function PaymentIntentApp() {
  const { t } = useTranslation();

  const [internalPage, setInternalPage] = useState<Page>();

  const pageToRender = useMemo(() => {
    const successUrl = PaymentStore.successUrl;
    switch (internalPage?.page) {
      case InternalPage.PaymentMethods:
        return <PaymentMethodsPage flow={PaymentStore.flow} />;
      case InternalPage.StatusError:
        return <ErrorHandler />;
      case InternalPage.StatusProcessing:
        return (
          <PaymentProcessing
            orderId={PaymentStore.orderNumber}
            title={
              PaymentStore.flow === PaymentIntentFlow.Manage
                ? t('Adding new payment method...')
                : t('Processing')
            }
          />
        );
      case InternalPage.StatusSuccess:
        return (
          <PaymentSuccessful
            orderId={PaymentStore.orderNumber}
            title={t('Payment successful!')}
            successUrl={successUrl}
          />
        );
      case InternalPage.StoredInstruments:
        return (
          <StoredInstrumentsPage
            instruments={PaymentStore.instruments}
            flow={PaymentStore.flow}
          />
        );
      case InternalPage.SessionExpired:
        return <SessionExpired />;
      case InternalPage.FinvoiceStatusSuccess:
        return (
          <PaymentSuccessful
            orderId={PaymentStore.orderNumber}
            title={t(
              'Your order will be processed after we have received your payment!',
            )}
            successUrl={successUrl}
          />
        );
      case InternalPage.PaymentOnDeliveryStatusSuccess:
        return (
          <PaymentSuccessful
            orderId={PaymentStore.orderNumber}
            title={t(
              'Pay for your order with Cash or Card once it is delivered',
            )}
            successUrl={successUrl}
          />
        );
      case InternalPage.PaymentInSubsidiaryStatusSuccess:
        return (
          <PaymentSuccessful
            orderId={PaymentStore.orderNumber}
            title={t(
              'Your order will be ready for pickup from the selected subsidiary. Payment for the order must be done at the time of the pickup',
            )}
            successUrl={successUrl}
          />
        );
      case InternalPage.PayByInvoiceStatusSuccess:
        return (
          <PaymentSuccessful
            orderId={PaymentStore.orderNumber}
            title={t('An invoice will be dispatched together with your order')}
            successUrl={successUrl}
          />
        );
      case InternalPage.BankTransferStatusSuccess:
        return (
          <PaymentSuccessful
            orderId={PaymentStore.orderNumber}
            title={t('Payment for the order must be done in advance.')}
            successUrl={successUrl}
          />
        );
      case InternalPage.BankTransfer:
        return <BankTransferDetailsPage />;
      case InternalPage.PaymentOnDelivery:
        return <PaymentOnDeliveryDetailsPage />;
      case InternalPage.Finvoice:
        return <FinvoiceDetailsPage />;
      case InternalPage.SepaMandateConsent:
        return <SepaMandateConsentPage />;
      case InternalPage.LsvMandateSummary:
        return <LsvMandateSummaryPage />;
      case InternalPage.PostFinanceMandateSummary:
        return <PostFinanceMandateSummaryPage />;
      default:
        return null;
    }
  }, [internalPage, t]);

  useEffect(() => {
    const navigationSubscription = NavigationService.subscribe(setInternalPage);
    return () => navigationSubscription.unsubscribe();
  }, []);

  return <>{pageToRender}</>;
}
